import { useId, useMemo } from 'react'

import { formatDate, formatNumber } from '../lib/format.ts'

export interface ChartPoint {
  /** Časová osa; očekává se datum ve formátu z API. */
  date: string
  value: number
  label?: string | null
  /**
   * Série, ze které bod vznikl. Když ho graf zná, dá se bod propojit
   * s řádkem ve výpisu pod grafem. Denní průměry ho nemají.
   */
  id?: number
}

const WIDTH = 720
const HEIGHT = 260
const PADDING = { top: 16, right: 16, bottom: 30, left: 46 }

/**
 * Vývoj metriky v čase. Vlastní SVG místo grafové knihovny — je to pár
 * desítek řádků, plně to sedí do designu a nezvětšuje to bundle.
 */
export function ProgressChart({
  points,
  unit,
  precision = 2,
  showTrend = true,
  highlightId = null,
  onHover,
  onSelect,
}: {
  points: ChartPoint[]
  unit?: string
  precision?: number
  showTrend?: boolean
  /** Bod odpovídající této sérii se zvýrazní. */
  highlightId?: number | null
  onHover?: (id: number | null) => void
  onSelect?: (id: number) => void
}): React.JSX.Element {
  const gradientId = useId()

  const chart = useMemo(() => build(points), [points])

  if (points.length === 0) {
    return <p className="muted">Zatím není co vykreslit — chybí dokončené série.</p>
  }

  const { scaled, ticks, trend } = chart

  const linePath = scaled.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  const areaPath = `${linePath} L ${scaled.at(-1)!.x} ${HEIGHT - PADDING.bottom} L ${scaled[0]!.x} ${HEIGHT - PADDING.bottom} Z`

  return (
    <div className="chart">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3ba7ff" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#3ba7ff" stopOpacity="0" />
          </linearGradient>
        </defs>

        <g className="chart__grid">
          {ticks.map((tick) => (
            <line key={tick.value} x1={PADDING.left} y1={tick.y} x2={WIDTH - PADDING.right} y2={tick.y} />
          ))}
        </g>

        <g className="chart__axis">
          {ticks.map((tick) => (
            <text key={tick.value} x={PADDING.left - 8} y={tick.y + 4} textAnchor="end">
              {formatNumber(tick.value, precision)}
            </text>
          ))}
        </g>

        {scaled.length > 1 && <path d={areaPath} fill={`url(#${gradientId})`} />}
        <path className="chart__line" d={linePath} />

        {showTrend && trend && (
          <line className="chart__trend" x1={trend.x1} y1={trend.y1} x2={trend.x2} y2={trend.y2} />
        )}

        {scaled.map((p) => {
          const interactive = p.id !== undefined
          const active = p.id !== undefined && p.id === highlightId

          return (
            <g
              key={`${p.date}-${p.x}`}
              onMouseEnter={() => interactive && onHover?.(p.id!)}
              onMouseLeave={() => interactive && onHover?.(null)}
              onClick={() => interactive && p.id !== undefined && onSelect?.(p.id)}
              style={{ cursor: interactive && onSelect ? 'pointer' : 'default' }}
            >
              {/* Průhledný terč: samotná tečka o poloměru 3,5 se myší trefuje bídně. */}
              <circle cx={p.x} cy={p.y} r={12} fill="transparent" />
              <circle
                className={`chart__dot ${active ? 'chart__dot--active' : ''}`}
                cx={p.x}
                cy={p.y}
                r={active ? 6 : 3.5}
              >
                <title>
                  {formatDate(p.date)} — {formatNumber(p.value, precision)}
                  {unit ? ` ${unit}` : ''}
                  {p.label ? ` · ${p.label}` : ''}
                </title>
              </circle>
            </g>
          )
        })}

        <g className="chart__axis">
          <text x={PADDING.left} y={HEIGHT - 8} textAnchor="start">
            {formatDate(points[0]!.date)}
          </text>
          {points.length > 1 && (
            <text x={WIDTH - PADDING.right} y={HEIGHT - 8} textAnchor="end">
              {formatDate(points.at(-1)!.date)}
            </text>
          )}
        </g>
      </svg>

      <div className="chart__legend">
        <span style={{ color: '#3ba7ff' }}>Výsledek série</span>
        {showTrend && trend && <span style={{ color: '#2fd07a' }}>Trend</span>}
      </div>
    </div>
  )
}

interface ScaledPoint extends ChartPoint {
  x: number
  y: number
}

function build(points: ChartPoint[]): {
  scaled: ScaledPoint[]
  ticks: { value: number; y: number }[]
  trend: { x1: number; y1: number; x2: number; y2: number } | null
} {
  if (points.length === 0) return { scaled: [], ticks: [], trend: null }

  const values = points.map((p) => p.value)
  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)

  // U jediné hodnoty (nebo samých stejných) by rozsah vyšel nulový.
  const span = rawMax - rawMin || Math.abs(rawMax) * 0.2 || 1
  const min = rawMin - span * 0.12
  const max = rawMax + span * 0.12

  const innerWidth = WIDTH - PADDING.left - PADDING.right
  const innerHeight = HEIGHT - PADDING.top - PADDING.bottom

  const toX = (index: number): number =>
    points.length === 1
      ? PADDING.left + innerWidth / 2
      : PADDING.left + (index / (points.length - 1)) * innerWidth

  const toY = (value: number): number =>
    PADDING.top + innerHeight - ((value - min) / (max - min)) * innerHeight

  const scaled: ScaledPoint[] = points.map((p, i) => ({ ...p, x: toX(i), y: toY(p.value) }))

  const tickCount = 4
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const value = min + ((max - min) * i) / tickCount
    return { value, y: toY(value) }
  })

  return { scaled, ticks, trend: buildTrend(points, toX, toY) }
}

/** Lineární regrese přes všechny body — ukazuje, kam se výkon dlouhodobě posouvá. */
function buildTrend(
  points: ChartPoint[],
  toX: (index: number) => number,
  toY: (value: number) => number,
): { x1: number; y1: number; x2: number; y2: number } | null {
  const n = points.length
  if (n < 3) return null

  let sumX = 0
  let sumY = 0
  let sumXY = 0
  let sumXX = 0

  points.forEach((p, i) => {
    sumX += i
    sumY += p.value
    sumXY += i * p.value
    sumXX += i * i
  })

  const denominator = n * sumXX - sumX * sumX
  if (denominator === 0) return null

  const slope = (n * sumXY - sumX * sumY) / denominator
  const intercept = (sumY - slope * sumX) / n

  return {
    x1: toX(0),
    y1: toY(intercept),
    x2: toX(n - 1),
    y2: toY(intercept + slope * (n - 1)),
  }
}

/** Sloupcový přehled jednotlivých pokusů v sérii — zelená = zvládnutý. */
export function TrialStrip({
  trials,
}: {
  trials: { difficulty: number; correct: boolean }[]
}): React.JSX.Element {
  const max = Math.max(...trials.map((t) => t.difficulty), 0.01)

  return (
    <div className="trials" aria-hidden="true">
      {trials.map((trial, i) => (
        <div
          key={i}
          className={`trials__bar ${trial.correct ? 'trials__bar--ok' : ''}`}
          style={{ height: `${Math.max(6, (trial.difficulty / max) * 100)}%` }}
          title={`Pokus ${i + 1}: ${formatNumber(trial.difficulty, 2)} — ${trial.correct ? 'správně' : 'chyba'}`}
        />
      ))}
    </div>
  )
}
